import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Toolbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Save as SaveIcon,
  Print as PrintIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useForm, Controller } from 'react-hook-form'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchInspection,
  createInspection,
  updateInspection,
  setSelectedInspection
} from '@store/slices/inspectionsSlice'
import { fetchChecklists } from '@store/slices/checklistsSlice'
import ChecklistInput from '@components/inspections/ChecklistInput'
import HiveSelector from '@components/inspections/HiveSelector'
import { Inspection, Checklist } from '@types'

interface InspectionFormData {
  date: Date
  hive_id: number | null
  checklist_id: number | undefined
  notes: string
  attention: boolean
  reminder: string
  reminder_date: Date | undefined
  impression: number
}

const Inspect: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  // This component renders on two different routes:
  //   /hives/:id/inspections/:inspectionId  -> `:id` is the hive id, `:inspectionId` the inspection
  //   /inspections/:id                      -> `:id` IS the inspection id (no hive id in the URL)
  // Route params only expose the raw `:id`/`:inspectionId` names, so disambiguate using the path.
  const routeParams = useParams<{ id?: string; inspectionId?: string }>()
  const isHiveScopedRoute = location.pathname.startsWith('/hives/')
  const hiveId = isHiveScopedRoute ? routeParams.id : undefined
  const inspectionId = isHiveScopedRoute ? routeParams.inspectionId : routeParams.id
  const dispatch = useAppDispatch()

  const { selectedInspection, loadingState, error } = useAppSelector((state) => state.inspections)
  const { checklists } = useAppSelector((state) => state.checklists)
  const { hives } = useAppSelector((state) => state.locations)

  // 'upload' mode ("Send Pictures") was removed - it only ever logged to the console and had
  // no UI to switch into it in the first place.
  const [mode] = useState<'online' | 'offline'>('online')
  const [showLoadingIcon, setShowLoadingIcon] = useState(false)
  const [selectedHives, setSelectedHives] = useState<number[]>([])
  const [selectedChecklist, setSelectedChecklistLocal] = useState<Checklist | null>(null)
  const [confirmDialog, setConfirmDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const editMode = Boolean(inspectionId)
  const offlineMode = mode === 'offline'
  const onlineMode = mode === 'online'

  const { control, handleSubmit, watch, setValue, formState: { errors, isValid } } = useForm<InspectionFormData>({
    defaultValues: {
      date: new Date(),
      hive_id: hiveId ? parseInt(hiveId) : null,
      checklist_id: undefined,
      notes: '',
      attention: false,
      reminder: '',
      reminder_date: undefined,
      impression: 2,
    },
  })

  const watchedHiveId = watch('hive_id')
  const watchedChecklistId = watch('checklist_id')

  useEffect(() => {
    // Load checklists
    dispatch(fetchChecklists())

    // Load inspection if editing
    if (inspectionId) {
      dispatch(fetchInspection(parseInt(inspectionId)))
    }

    // Set initial hive from location state
    const stateHiveId = location.state?.hiveId
    if (stateHiveId && !hiveId) {
      setValue('hive_id', stateHiveId)
      setSelectedHives([stateHiveId])
    } else if (hiveId) {
      setSelectedHives([parseInt(hiveId)])
    }
  }, [dispatch, inspectionId, hiveId, location.state, setValue])

  useEffect(() => {
    // Update form when inspection is loaded
    if (selectedInspection && editMode) {
      setValue('date', new Date(selectedInspection.date))
      setValue('hive_id', selectedInspection.hive_id)
      // The backend returns the checklist link as `inspection_type_id` (aliasing `template.id`),
      // not `checklist_id` - fall back to it so the checklist dropdown pre-selects correctly.
      setValue('checklist_id', selectedInspection.checklist_id ?? (selectedInspection as any).inspection_type_id)
      setValue('notes', selectedInspection.notes || '')
      setValue('attention', selectedInspection.attention)
      setValue('reminder', selectedInspection.reminder || '')
      setValue('reminder_date', selectedInspection.reminder_date ? new Date(selectedInspection.reminder_date) : undefined)
      setValue('impression', selectedInspection.impression)
      setSelectedHives([selectedInspection.hive_id])
    }
  }, [selectedInspection, editMode, setValue])

  useEffect(() => {
    // Update selected checklist when checklist_id changes
    if (watchedChecklistId) {
      const checklist = checklists.find(c => c.id === watchedChecklistId)
      setSelectedChecklistLocal(checklist || null)
    }
  }, [watchedChecklistId, checklists])

  const getTitle = () => {
    if (editMode) return 'Edit Inspection'
    const modeText = offlineMode ? ' (Offline Mode)' : ''
    return `New Inspection${modeText}`
  }

  const getSelectedHive = () => {
    return hives.find(h => h.id === watchedHiveId)
  }

  const isHiveEditable = () => {
    const hive = getSelectedHive()
    return hive ? (hive.owner || hive.editable) : true
  }

  const isInspectionEditable = () => {
    if (!editMode || !selectedInspection) return true
    // The backend's InspectionSerializer doesn't return `owner`/`editable` at all (unlike
    // Hive), and its queryset is already scoped to `user=request.user` (see
    // apps.inspections.views.InspectionViewSet.get_queryset), so any inspection this page can
    // load is by definition the current user's own and editable. Only treat it as locked if a
    // future backend response explicitly says so.
    return selectedInspection.owner !== false && selectedInspection.editable !== false
  }

  const handleSaveInspection = async (data: InspectionFormData) => {
    if (!data.hive_id) {
      setErrorMessage('Please select a hive')
      return
    }

    setShowLoadingIcon(true)
    setErrorMessage('')

    try {
      const inspectionData: Partial<Inspection> = {
        date: data.date.toISOString(),
        hive_id: data.hive_id,
        checklist_id: data.checklist_id || undefined,
        notes: data.notes,
        attention: data.attention,
        reminder: data.reminder,
        reminder_date: data.reminder_date?.toISOString() || undefined,
        impression: data.impression,
      }

      if (editMode && selectedInspection) {
        await dispatch(updateInspection({ id: selectedInspection.id, data: inspectionData }))
      } else {
        await dispatch(createInspection(inspectionData))
      }

      navigate('/inspections')
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to save inspection')
    } finally {
      setShowLoadingIcon(false)
    }
  }

  const handleConfirmSave = () => {
    setConfirmDialog(true)
  }

  const handleConfirmedSave = () => {
    setConfirmDialog(false)
    handleSubmit(handleSaveInspection)()
  }

  const handlePrint = () => {
    window.print()
  }

  const handleEditChecklist = () => {
    // There's no per-checklist editor route (only /checklists and /checklists/create exist),
    // so send the user to the checklists list rather than a dead `/checklists/:id/edit` link.
    navigate('/checklists')
  }

  if (!isHiveEditable()) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Sorry, hive "{getSelectedHive()?.name}" is not editable
        </Typography>
      </Container>
    )
  }

  if (editMode && selectedInspection && !isInspectionEditable()) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Sorry, inspection {inspectionId} is not editable
        </Typography>
      </Container>
    )
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Save Bar */}
        <Toolbar
          sx={{
            position: 'fixed',
            top: 148,
            left: 0,
            right: 0,
            zIndex: 4,
            backgroundColor: '#fffaf0',
            borderBottom: '1px solid #fff5e2',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {selectedChecklist?.owner && !isMobile && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EditIcon />}
              onClick={handleEditChecklist}
            >
              Edit Checklist
            </Button>
          )}

          {onlineMode && (
            <Button
              variant="outlined"
              startIcon={showLoadingIcon ? <CircularProgress size={18} /> : <SaveIcon />}
              disabled={!isValid || selectedHives.length === 0 || showLoadingIcon}
              onClick={handleConfirmSave}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Save
            </Button>
          )}

          {offlineMode && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Print
            </Button>
          )}

        </Toolbar>

        {/* Main Content */}
        <Container sx={{ pt: 8, pb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {getTitle()}
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(handleSaveInspection)}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Inspection Details
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="date"
                        control={control}
                        rules={{ required: 'Date is required' }}
                        render={({ field }) => (
                          <DateTimePicker
                            label="Inspection Date"
                            value={field.value}
                            onChange={field.onChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.date,
                                helperText: errors.date?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <HiveSelector
                        selectedHives={selectedHives}
                        onSelectionChange={(hiveIds) => {
                          setSelectedHives(hiveIds)
                          setValue('hive_id', hiveIds[0] || null)
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Checklist</InputLabel>
                        <Controller
                          name="checklist_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={field.value || ''}
                              label="Checklist"
                            >
                              <MenuItem value="">No Checklist</MenuItem>
                              {checklists.map((checklist) => (
                                <MenuItem key={checklist.id} value={checklist.id}>
                                  {checklist.name}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="impression"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel>Overall Impression</InputLabel>
                            <Select {...field} label="Overall Impression">
                              <MenuItem value={1}>Poor</MenuItem>
                              <MenuItem value={2}>Fair</MenuItem>
                              <MenuItem value={3}>Good</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="attention"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            }
                            label="Needs Attention"
                          />
                        )}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Notes and Reminders */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Notes & Reminders
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            label="Notes"
                            placeholder="Add your inspection notes here..."
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="reminder"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Reminder"
                            placeholder="Set a reminder for next inspection..."
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="reminder_date"
                        control={control}
                        render={({ field }) => (
                          <DateTimePicker
                            label="Reminder Date"
                            value={field.value}
                            onChange={field.onChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Checklist Items */}
              {selectedChecklist && (
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {selectedChecklist.name}
                      </Typography>

                      {selectedChecklist.categories?.map((category) => (
                        <ChecklistInput
                          key={category.id}
                          category={category}
                          inspection={selectedInspection}
                        />
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </form>
        </Container>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save this inspection?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmedSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default Inspect
